/**
 * Pure Page_Mapping navigation helpers for the WBR viewer
 * (pdf-margin-callouts).
 *
 * The Margin_Callout_PDF inserts Overflow_Pages directly after dense pages,
 * so its page indices no longer equal Original_Page_Numbers. The generator
 * writes a sidecar JSON (`{stem}_margin_callouts_map.json`) describing the
 * mapping and the callout service returns it through the AWSJSON
 * `margin_page_mapping` field. This module parses that payload and converts
 * between the two coordinate systems: callout-card clicks and `?page=N` deep
 * links resolve Original_Page_Numbers to variant page indices (Req 10.2),
 * while cell-selection interactions resolve variant pages back to
 * Original_Page_Numbers — including overflow pages, which resolve to the
 * page whose callout cards they hold (Req 10.4, 10.6). A missing or
 * malformed mapping degrades to identity navigation rather than an error
 * (Req 10.8). Extracted as a pure module so the round-trip behavior is
 * directly unit/property testable (mirrors the `wbrVariantSelection.ts` and
 * `canEditSummary.ts` extraction pattern).
 */

/**
 * Page_Mapping of a Margin_Callout_PDF that contains Overflow_Pages: the
 * camelCase form of the generator's sidecar JSON. All page numbers are
 * 1-based.
 */
export interface MarginPageMapping {
  /**
   * Sidecar `original_to_variant`: Original_Page_Number -> variant page
   * index of that original page's content (Req 10.2).
   */
  originalToVariant: Record<string, number>;
  /**
   * Sidecar `overflow_pages`: overflow variant page index -> the
   * Original_Page_Number whose callout cards that page holds (Req 10.6).
   */
  overflowPages: Record<string, number>;
  /** Sidecar `total_variant_pages`: page count of the margin variant. */
  totalVariantPages: number;
  /** Sidecar `original_page_count`: page count of the Source_PDF. */
  originalPageCount: number;
}

/** A 1-based page number: a positive integer. */
function isPageNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

/** A plain object whose keys and values are all 1-based page numbers. */
function isPageNumberRecord(value: unknown): value is Record<string, number> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.entries(value).every(
    ([key, page]) => isPageNumber(Number(key)) && isPageNumber(page)
  );
}

/**
 * Parse the AWSJSON `margin_page_mapping` field (or an already-parsed
 * sidecar object) into a `MarginPageMapping`.
 *
 * Accepts the snake_case sidecar shape (`original_to_variant`,
 * `overflow_pages`, `total_variant_pages`, `original_page_count`) either as
 * a JSON string — tolerating one extra encoding level, matching the
 * viewer's `page_offset_map` parse — or as an object. Returns null for any
 * malformed payload (unparseable JSON, missing fields, non-page-number
 * entries), which callers treat as "no mapping" so navigation falls back to
 * identity (Req 10.8).
 *
 * @param raw The raw `margin_page_mapping` value from GraphQL, or a parsed
 *   sidecar object.
 * @returns The camelCase mapping, or null when the payload is malformed.
 */
export function parseMarginPageMapping(raw: unknown): MarginPageMapping | null {
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
      // AWSJSON payloads sometimes arrive double-encoded; tolerate one
      // extra level, as the page_offset_map parse does.
      if (typeof value === "string") value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const sidecar = value as Record<string, unknown>;
  const originalToVariant = sidecar.original_to_variant;
  const overflowPages = sidecar.overflow_pages;
  const totalVariantPages = sidecar.total_variant_pages;
  const originalPageCount = sidecar.original_page_count;
  if (
    !isPageNumberRecord(originalToVariant) ||
    !isPageNumberRecord(overflowPages) ||
    !isPageNumber(totalVariantPages) ||
    !isPageNumber(originalPageCount)
  ) {
    return null;
  }
  return {
    originalToVariant: { ...originalToVariant },
    overflowPages: { ...overflowPages },
    totalVariantPages,
    originalPageCount,
  };
}

/**
 * The variant page index that renders the content of Original_Page_Number
 * `original`.
 *
 * Resolves through `originalToVariant` so callout-card clicks and `?page=N`
 * deep links land on the right page of a variant containing Overflow_Pages
 * (Req 10.2). A null mapping or a missing entry returns `original`
 * unchanged — the identity fallback that keeps navigation functional
 * without a mapping (Req 10.8).
 *
 * @param m The displayed variant's Page_Mapping, or null when none exists.
 * @param original A 1-based Original_Page_Number.
 * @returns The 1-based variant page index, or `original` as identity.
 */
export function toVariantPage(
  m: MarginPageMapping | null,
  original: number
): number {
  if (m === null) return original;
  const variant = m.originalToVariant[String(original)];
  return isPageNumber(variant) ? variant : original;
}

/**
 * The Original_Page_Number that variant page index `variant` reports as.
 *
 * An overflow variant page resolves through `overflowPages` to the
 * Original_Page_Number whose callout cards it holds (Req 10.6); a content
 * page resolves through the inverse of `originalToVariant`, so
 * cell-selection interactions key the original page rather than the
 * variant index (Req 10.4). A null mapping or a variant index found in
 * neither record returns `variant` unchanged (identity, Req 10.8).
 *
 * @param m The displayed variant's Page_Mapping, or null when none exists.
 * @param variant A 1-based page index in the displayed variant.
 * @returns The 1-based Original_Page_Number, or `variant` as identity.
 */
export function toOriginalPage(
  m: MarginPageMapping | null,
  variant: number
): number {
  if (m === null) return variant;
  // Overflow page: reports as the page whose cards it holds (Req 10.6).
  const overflowSource = m.overflowPages[String(variant)];
  if (isPageNumber(overflowSource)) return overflowSource;
  // Content page: invert originalToVariant (Req 10.4).
  for (const [original, variantIndex] of Object.entries(m.originalToVariant)) {
    if (variantIndex === variant) return Number(original);
  }
  return variant;
}
